import React from 'react';

/**
 * Read-only price breakdown for the welding module, shown inline on the welding
 * tab so it reads like the other modules' breakdown panels. It renders the
 * welding calculator's existing result (hardware, software, mobilization, the
 * recurring other costs, and the total) and, line by line, the derivation
 * behind each figure. It does not change how welding is priced. When no price
 * has been calculated yet, it shows a prompt.
 */

export interface WeldingBreakdownDetails {
  durationMonths?: number;
  totalQuantity?: number;
  actualUnits?: number;
  spareUnits?: number;
  softwareChargeableUnits?: number;
  hardwareCost?: number;
  softwareCost?: number;
  mobilizationCost?: number;
  basePrice?: number;
  discountPercentage?: number;
  otherCosts?: {
    internet?: number;
    cloud?: number;
    cloudCompute?: number;
    cloudStorage?: number;
    remoteSupport?: number;
    training?: number;
    provisioning?: number;
    onFieldSupport?: number;
  };
  totalOtherCosts?: number;
  totalPrice?: number;
  modeMix?: {
    weightedConnectedUnits?: number;
  };
}

interface Props {
  details?: WeldingBreakdownDetails;
}

const currency = (n?: number): string =>
  `$${(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Row: React.FC<{ label: string; value?: number; calc?: string; bold?: boolean }> = ({
  label,
  value,
  calc,
  bold,
}) => (
  <div className="py-0.5">
    <div className={`flex justify-between ${bold ? 'font-semibold text-text-grey' : 'text-text-grey'}`}>
      <span>{label}</span>
      <span>{currency(value)}</span>
    </div>
    {calc ? <div className="text-xs text-heritage-grey">{calc}</div> : null}
  </div>
);

export const WeldingBreakdownPanel: React.FC<Props> = ({ details }) => {
  if (!details) {
    return (
      <div className="rounded-xl border border-[#E2E5EA] bg-page-bg p-4 text-sm text-heritage-grey">
        Click Calculate Price to see the welding breakdown.
      </div>
    );
  }

  const other = details.otherCosts ?? {};
  const months = details.durationMonths ?? 0;
  const hwUnits = details.totalQuantity ?? 0;
  const swUnits = details.softwareChargeableUnits ?? 0;
  const discount = details.discountPercentage ?? 0;
  // Internet and cloud are charged per connected unit per month, scaled by the
  // live/offline mode mix, so their derivation uses the weighted connected units.
  const connectedUnits = details.modeMix?.weightedConnectedUnits ?? hwUnits;

  // Effective per-unit-per-month and per-month rates, recovered from the line
  // totals so each figure shows the same arithmetic the other modules display.
  const perUnitMonth = (total: number | undefined, units: number): string => {
    if (!total || units <= 0 || months <= 0) return '';
    const rate = total / (units * months);
    return `${units} units x ${currency(rate)}/mo x ${months} mo`;
  };
  const perMonth = (total: number | undefined): string => {
    if (!total || months <= 0) return '';
    return `${currency(total / months)}/mo x ${months} mo`;
  };
  // The duration discount applies to hardware and software, so both note it.
  const withDiscount = (base: string): string =>
    base && discount > 0 ? `${base} (after ${discount}% discount)` : base;
  const hardwareCalc = withDiscount(perUnitMonth(details.hardwareCost, hwUnits));
  const softwareCalc = withDiscount(perUnitMonth(details.softwareCost, swUnits));

  const hasOther =
    [other.internet, other.cloud, other.remoteSupport, other.training, other.provisioning, other.onFieldSupport]
      .some((v) => (v ?? 0) > 0);

  return (
    <div className="rounded-xl border border-[#E2E5EA] p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-medium font-heading text-blueprint-blue">Price breakdown</h3>
        {discount ? (
          <span className="text-xs text-heritage-grey">{discount}% duration discount applied</span>
        ) : null}
      </div>

      <div className="space-y-1 text-sm">
        <Row label="Hardware" value={details.hardwareCost} calc={hardwareCalc} />
        <Row label="DATA 360 Platform License" value={details.softwareCost} calc={softwareCalc} />
        <Row label="Mobilization" value={details.mobilizationCost} />
        <div className="my-2 border-t border-[#EEF0F3]" />
        <Row label="Base price" value={details.basePrice} bold />

        {hasOther && (
          <>
            <div className="my-2 border-t border-[#EEF0F3]" />
            {(other.internet ?? 0) > 0 && <Row label="Internet" value={other.internet} calc={perUnitMonth(other.internet, connectedUnits)} />}
            {(other.cloudCompute ?? 0) > 0 || (other.cloudStorage ?? 0) > 0 ? (
              <>
                {(other.cloudCompute ?? 0) > 0 && <Row label="Cloud compute" value={other.cloudCompute} calc={perUnitMonth(other.cloudCompute, connectedUnits)} />}
                {(other.cloudStorage ?? 0) > 0 && <Row label="Cloud storage" value={other.cloudStorage} calc={perUnitMonth(other.cloudStorage, connectedUnits)} />}
              </>
            ) : (
              (other.cloud ?? 0) > 0 && <Row label="Cloud" value={other.cloud} calc={perUnitMonth(other.cloud, connectedUnits)} />
            )}
            {(other.remoteSupport ?? 0) > 0 && <Row label="Remote support" value={other.remoteSupport} calc={perMonth(other.remoteSupport)} />}
            {(other.training ?? 0) > 0 && <Row label="Training" value={other.training} />}
            {(other.provisioning ?? 0) > 0 && <Row label="Provisioning" value={other.provisioning} />}
            {(other.onFieldSupport ?? 0) > 0 && <Row label="On-field support" value={other.onFieldSupport} />}
            <Row label="Total other costs" value={details.totalOtherCosts} bold />
          </>
        )}

        <div className="my-2 border-t border-[#E2E5EA]" />
        <Row label="Welding total" value={details.totalPrice} bold />
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-heritage-grey">
        <span>Duration: {months} months</span>
        <span>Total units: {hwUnits}</span>
        <span>Actual: {details.actualUnits ?? 0}</span>
        <span>Spares: {details.spareUnits ?? 0}</span>
      </div>
    </div>
  );
};
